import { useEffect, useState } from 'react';
import { Router } from 'react-router-dom';
import { createBrowserHistory, History, Location, Action } from 'history';
import { wrapHistory } from 'oaf-react-router';
import { createKgNavigate } from '../utils/kgNavigate';

declare global {
	interface Window {
		/**
		 * Lets the chat widget move the visitor to another shop page as an SPA navigation, exactly
		 * like clicking a router Link. Returns true when it navigated, false when it ignored the path.
		 * Only present while the client router is mounted; the widget falls back to location.assign.
		 */
		__kgNavigate?: (path: string) => boolean;
	}
}

let clientHistory: History | undefined;

/*
 * One history for the lifetime of the page, created lazily so nothing touches `window` at module
 * scope. Wrapping it more than once would stack oaf's listeners and block handlers, so the wrap
 * lives here with the creation rather than in the render body.
 */
const getClientHistory = (): History => {
	if (!clientHistory) {
		clientHistory = createBrowserHistory();
		wrapHistory(clientHistory, {
			shouldHandleAction(_previous: Location, _next: Location, action?: Action) {
				// when loading more data on a paginated page we REPLACE the history state.
				// don't change scroll position or focus in those cases.
				return action !== 'REPLACE';
			},
			primaryFocusTarget: 'app',
		});
	}
	return clientHistory;
};

export const ClientRouter = ({ children }: { children: JSX.Element }) => {
	// getClientHistory returns one instance per module evaluation. A Fast Refresh edit re-evaluates
	// this module and empties that cache, but keeps hook state, so holding the history in state keeps
	// the Router on the instance it mounted with instead of creating and wrapping a second one
	const [history] = useState(getClientHistory);

	useEffect(() => {
		const navigate = createKgNavigate(history);
		window.__kgNavigate = navigate;
		return () => {
			if (window.__kgNavigate === navigate) delete window.__kgNavigate;
		};
	}, [history]);

	return <Router history={history}>{children}</Router>;
};
